import React, { useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowUpRight, Lock } from "lucide-react";
import { Reveal } from "../components/motion";
import { Seo } from "../components/Seo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const field = "mt-3 w-full bg-transparent border-b py-3 outline-none transition-colors text-lg";

const BlogNew = () => {
  const navigate = useNavigate();
  const [form, setForm] = useState({ title: "", excerpt: "", cover_image: "", tags: "", content: "" });
  const [authorKey, setAuthorKey] = useState("");
  const [loading, setLoading] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.title.trim() || !form.content.trim()) {
      toast.error("Title and content are required.");
      return;
    }
    if (!authorKey.trim()) {
      toast.error("Enter your author key to publish.");
      return;
    }
    setLoading(true);
    try {
      const payload = {
        title: form.title.trim(),
        excerpt: form.excerpt.trim(),
        cover_image: form.cover_image.trim(),
        content: form.content,
        tags: form.tags.split(",").map((t) => t.trim()).filter(Boolean),
      };
      const { data } = await axios.post(`${API}/blog`, payload, { headers: { "X-Author-Key": authorKey.trim() } });
      toast.success("Published!");
      navigate(`/blog/${data.slug}`);
    } catch (err) {
      const status = err?.response?.status;
      toast.error(status === 401 ? "Invalid author key." : "Could not publish. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="blog-new-page">
      <Seo path="/blog/new" description="Authoring space for Yuva Kumar's journal." />

      <section className="pt-40 pb-16">
        <div className="mx-auto max-w-[860px] px-6 sm:px-10">
          <Reveal><p className="eyebrow mb-6">Authoring</p></Reveal>
          <Reveal delay={0.1}>
            <h1 className="display text-5xl sm:text-7xl" style={{ color: "var(--text)" }}>Write a new post.</h1>
          </Reveal>
        </div>
      </section>

      <section className="pb-32">
        <div className="mx-auto max-w-[860px] px-6 sm:px-10">
          <form onSubmit={submit} className="space-y-8" data-testid="blog-new-form">
            <label className="block">
              <span className="text-xs uppercase tracking-widest" style={{ color: "var(--muted-t)" }}>Title *</span>
              <input value={form.title} onChange={set("title")} data-testid="blog-title" placeholder="e.g. Designing With Agentic AI" className={field} style={{ borderColor: "var(--line)", color: "var(--text)" }} />
            </label>

            <label className="block">
              <span className="text-xs uppercase tracking-widest" style={{ color: "var(--muted-t)" }}>Excerpt</span>
              <input value={form.excerpt} onChange={set("excerpt")} data-testid="blog-excerpt" placeholder="A one-line summary shown on cards" className={field} style={{ borderColor: "var(--line)", color: "var(--text)" }} />
            </label>

            <div className="grid sm:grid-cols-2 gap-8">
              <label className="block">
                <span className="text-xs uppercase tracking-widest" style={{ color: "var(--muted-t)" }}>Cover image URL</span>
                <input value={form.cover_image} onChange={set("cover_image")} data-testid="blog-cover" placeholder="https://images.unsplash.com/..." className={field} style={{ borderColor: "var(--line)", color: "var(--text)" }} />
              </label>
              <label className="block">
                <span className="text-xs uppercase tracking-widest" style={{ color: "var(--muted-t)" }}>Tags (comma separated)</span>
                <input value={form.tags} onChange={set("tags")} data-testid="blog-tags" placeholder="UX Strategy, Design Ops" className={field} style={{ borderColor: "var(--line)", color: "var(--text)" }} />
              </label>
            </div>

            <label className="block">
              <span className="text-xs uppercase tracking-widest" style={{ color: "var(--muted-t)" }}>Content * <span className="normal-case tracking-normal">(leave a blank line between paragraphs)</span></span>
              <textarea value={form.content} onChange={set("content")} data-testid="blog-content" rows={14} placeholder="Write your post here..." className="mt-3 w-full bg-transparent border-b py-3 outline-none resize-y transition-colors text-lg leading-relaxed" style={{ borderColor: "var(--line)", color: "var(--text)" }} />
            </label>

            <label className="block max-w-sm">
              <span className="text-xs uppercase tracking-widest flex items-center gap-2" style={{ color: "var(--muted-t)" }}><Lock size={12} style={{ color: "var(--accent)" }} /> Author key *</span>
              <input type="password" value={authorKey} onChange={(e) => setAuthorKey(e.target.value)} data-testid="blog-author-key" placeholder="Your publishing key" className={field} style={{ borderColor: "var(--line)", color: "var(--text)" }} />
            </label>

            <button type="submit" disabled={loading} data-testid="blog-publish" className="group inline-flex items-center gap-4 px-9 py-5 rounded-full transition-opacity disabled:opacity-40" style={{ background: "var(--accent)", color: "var(--bg)" }}>
              <span className="text-sm uppercase tracking-widest">{loading ? "Publishing..." : "Publish Post"}</span>
              <ArrowUpRight size={18} className="transition-transform group-hover:translate-x-1 group-hover:-translate-y-1" />
            </button>
          </form>
        </div>
      </section>
    </div>
  );
};

export default BlogNew;
